import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertVariant,
  Button,
  Card,
  CardBody,
  CardTitle,
  PageSection,
  Spinner,
  Title,
} from '@patternfly/react-core';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../../api';

interface InviteData {
  id: number;
  clientID: number;
  firstname: string;
  lastname: string;
  sub: string | null;
  email: string;
  status: string;
  invite_token: string;
  invite_expires: string;
}

interface InviteValidationSuccess {
  status: string;
  data: InviteData[];
  message?: string;
  error?: string;
}

type InviteValidationState =
  | { status: 'idle' | 'loading' }
  | { status: 'invalid'; message: string }
  | { status: 'error'; message: string }
  | { status: 'valid'; data: InviteData };

const AcceptInvite: React.FC = () => {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const [state, setState] = useState<InviteValidationState>({ status: 'idle' });

  const token = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    return t ?? '';
  }, []);

  useEffect(() => {
    const validate = async () => {
      if (!token) {
        setState({ status: 'invalid', message: 'Missing invitation token in the URL.' });
        return;
      }

      setState({ status: 'loading' });
      try {
        const res = await apiFetch('/users/invite/validate', {
          method: 'POST',
          body: JSON.stringify({ token }),
        });

        let responseData: InviteValidationSuccess | null = null;
        try {
          responseData = await res.json();
        } catch {
          // non-JSON response
        }

        if (!res.ok) {
          const msg = responseData?.message || responseData?.error || 'Invitation is invalid or has expired.';
          setState({ status: 'invalid', message: msg });
          return;
        }

        // Check if we have valid data
        if (
          !responseData ||
          responseData.status !== 'success' ||
          !responseData.data ||
          responseData.data.length === 0
        ) {
          setState({ status: 'invalid', message: 'Invalid invitation response.' });
          return;
        }

        const inviteData = responseData.data[0];

        // Store the token for later use
        sessionStorage.setItem('pendingInvite', token);
        // Store the invite data for post-registration processing
        sessionStorage.setItem('inviteData', JSON.stringify(inviteData));

        setState({ status: 'valid', data: inviteData });

        // Redirect to registration with proper email
        setTimeout(() => {
          redirectToRegistration(inviteData.email);
        }, 500);
      } catch (e) {
        console.error('Error validating invite token', e);
        setState({ status: 'error', message: 'Network error while validating the invitation. Please try again.' });
      }
    };

    void validate();
  }, [auth, token, apiFetch]);

  const redirectToRegistration = async (email: string) => {
    const settings = auth.settings;

    // Keycloak registration endpoint - note the path
    const authority = settings.authority || '';

    // For custom flows, you might need to use auth endpoint with specific parameters
    const authUrl = `${authority}/protocol/openid-connect/auth`;

    // Generate PKCE parameters
    const { codeVerifier, codeChallenge } = await generatePKCE();

    // Store code_verifier for the callback
    sessionStorage.setItem('oidc.code_verifier', codeVerifier);

    const params = new URLSearchParams({
      client_id: settings.client_id || '',
      redirect_uri: settings.redirect_uri || window.location.origin + '/auth/callback',
      response_type: settings.response_type || 'code',
      scope: settings.scope || 'openid email profile',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      // This is crucial - it tells Keycloak to use your custom registration flow
      kc_action: 'register',
      // Specify the custom flow if needed
      kc_idp_hint: 'google', // If you want to suggest Google login
    });

    // Pre-fill the email
    if (email) {
      params.set('login_hint', email);
    }

    // Add state and nonce for security
    const state = generateRandomString();
    const nonce = generateRandomString();
    sessionStorage.setItem('oidc.state', state);
    sessionStorage.setItem('oidc.nonce', nonce);

    params.set('state', state);
    params.set('nonce', nonce);

    // Redirect to registration
    window.location.href = `${authUrl}?${params.toString()}`;
  };

  const generateRandomString = (): string => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
  };

  const generatePKCE = async (): Promise<{ codeVerifier: string; codeChallenge: string }> => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const codeVerifier = base64UrlEncode(array);

    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const hash = await crypto.subtle.digest('SHA-256', data);
    const codeChallenge = base64UrlEncode(new Uint8Array(hash));

    return { codeVerifier, codeChallenge };
  };

  const base64UrlEncode = (array: Uint8Array): string => {
    let binary = '';
    for (let i = 0; i < array.length; i++) {
      binary += String.fromCharCode(array[i]);
    }
    const base64 = btoa(binary);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  };

  const goHome = () => {
    window.location.href = '/';
  };

  return (
    <PageSection isFilled>
      <Card isFullHeight>
        <CardTitle>
          <Title headingLevel="h2">Accept Invitation</Title>
        </CardTitle>
        <CardBody>
          {state.status === 'idle' || state.status === 'loading' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Spinner size="lg" />
              <span>Validating your invitation…</span>
            </div>
          ) : null}

          {state.status === 'invalid' ? (
            <>
              <Alert variant={AlertVariant.danger} title="Invalid or expired invitation" isInline>
                {state.message}
              </Alert>
              <div style={{ marginTop: 16 }}>
                <Button onClick={goHome} variant="primary">
                  Go to homepage
                </Button>
              </div>
            </>
          ) : null}

          {state.status === 'error' ? (
            <>
              <Alert variant={AlertVariant.warning} title="Unable to validate invitation" isInline>
                {state.message}
              </Alert>
              <div style={{ marginTop: 16 }}>
                <Button onClick={() => window.location.reload()} variant="primary">
                  Retry
                </Button>
                <Button onClick={goHome} variant="link" style={{ marginLeft: 8 }}>
                  Go to homepage
                </Button>
              </div>
            </>
          ) : null}

          {state.status === 'valid' ? (
            <Alert variant={AlertVariant.info} title="Invitation validated" isInline>
              Redirecting you to register with email: {state.data.email}…
            </Alert>
          ) : null}
        </CardBody>
      </Card>
    </PageSection>
  );
};

export default AcceptInvite;
